import { useCallback, useRef } from 'react';
import { onlyDateFromServer,useMutation } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toNumber,toString } from 'lodash';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { API_URL, IOcrResultDto } from '@/dtos';
import { OcrResult } from '@/modules/UseMyMoney/UseMyMoney.types';
import { formatOcrAccountOrder, getAccountOrdersArraysWithUniqServices, getAvailableAccounts } from '@/modules/UseMyMoney/utils';

interface Payload {
  documentId: string;
}

const useGetDocumentOcrQuery = () => {
  const controllerRef = useRef<AbortController | null>(null);

  const { mutateAsync, isLoading } = useMutation((payload: Payload) => {
    controllerRef.current = new AbortController();
    return api.post<IOcrResultDto>(
      API_URL.DOCUMENTS_OCR_BY_DOCUMENT_ID(payload.documentId),
      {},
      {
        signal: controllerRef.current.signal,
      }
    );
  });

  const handleGetDocumentOcr = useCallback(async (payload: Payload): Promise<OcrResult | undefined> => {
    if (!payload?.documentId) {
      return;
    }

    const response = await mutateAsync(payload);
    const ocr = response?.data;
    const accountOrders = ocr?.account_orders?.flat()?.map(formatOcrAccountOrder);
    const availableAccounts = getAvailableAccounts(accountOrders);
    const accountOrdersArraysWithUniqServices = getAccountOrdersArraysWithUniqServices(ocr?.account_orders);

    return {
      selectedProvider: {
        providerId: toString(ocr?.selected_provider?.provider_id),
        name: ocr?.selected_provider?.name,
        taxId: ocr?.selected_provider?.tax_id,
        isNewProvider: ocr?.selected_provider?.is_new_provider,
      },
      selectedServiceFor: {
        id: ocr?.selected_service_for?.dependent_id
          ? toString(ocr?.selected_service_for?.dependent_id)
          : toString(ocr?.selected_service_for?.employee_id),
        employeeId: toString(ocr?.selected_service_for?.employee_id),
        dependentId: toString(ocr?.selected_service_for?.dependent_id),
        fullName: ocr?.selected_service_for?.full_name,
        relationshipType: ocr?.selected_service_for?.relationship_type,
        birthOn: ocr?.selected_service_for?.birth_on
           ? onlyDateFromServer(`${ocr?.selected_service_for?.birth_on}`)
           : undefined,
        isSuggested: ocr?.selected_service_for?.is_suggested,
        receiptName: ocr?.selected_service_for?.receipt_name,
      },
      selectedService: {
        serviceId: toString(ocr?.selected_service?.service_id),
        serviceName: ocr?.selected_service?.service_name,
        isSuggested: ocr?.selected_service?.is_suggested,
      },
      selectedAmount: {
        amount: ocr?.selected_amount?.amount !== undefined 
          ? toString(toNumber(ocr?.selected_amount?.amount).toFixed(2)) 
          : undefined,
        isSuggested: ocr?.selected_amount?.is_suggested,
      },
      selectedServiceDate: {
        date: ocr?.selected_service_date?.date
          ? dayjs(onlyDateFromServer(`${ocr?.selected_service_date?.date}`)).format(DEFAULT_DATE_FORMAT)
          : undefined,
        isSuggested: ocr?.selected_service_date?.is_suggested,
      },
      selectedAccountNumber: ocr?.selected_account_number,
      selectedInvoiceNumber: ocr?.selected_invoice_number,
      providers: ocr?.providers?.map((provider) => ({
        id: toString(provider?.id),
        name: provider?.name,
        isSuggested: provider?.is_suggested,
        address: {
          city: provider?.address?.city,
          line1: provider?.address?.line1,
          line2: provider?.address?.line2,
          zipcode: provider?.address?.zip,
          state: {
            id: toString(provider?.address?.state?.id),
            code: provider?.address?.state?.code,
            name: provider?.address?.state?.name,
          },
        },
      })),
      addressOptions: ocr?.address_options?.map((address) => ({
        line1: address?.line1,
        line2: address?.line2,
        city: address?.city,
        state: {
          id: toString(address?.state?.id),
          code: address?.state?.code,
          name: address?.state?.name,
        },
        zipcode: address?.zip,
      })),
      serviceForOptions: ocr?.service_for_options?.map((serviceFor) => ({
        id: serviceFor?.dependent_id
          ? toString(serviceFor?.dependent_id)
          : toString(serviceFor?.employee_id),
        employeeId: toString(serviceFor?.employee_id),
        dependentId: toString(serviceFor?.dependent_id),
        fullName: serviceFor?.full_name,
        relationshipType: serviceFor?.relationship_type,
        birthOn: serviceFor?.birth_on
          ? onlyDateFromServer(`${serviceFor?.birth_on}`)
          : undefined,
        isSuggested: serviceFor?.is_suggested,
        receiptName: serviceFor?.receipt_name,
      })),
      serviceOptions: ocr?.service_options?.map((service) => ({
        serviceId: toString(service?.service_id),
        serviceName: service?.service_name,
        isSuggested: service?.is_suggested,
        categoryType: service?.category_type,
      })),
      amountOptions: ocr?.amount_options?.map((amount) => ({
        amount: amount?.amount !== undefined
          ? toString(toNumber(amount?.amount).toFixed(2)) 
          : undefined,
        isSuggested: amount?.is_suggested,
      })),
      serviceDateOptions: ocr?.service_date_options?.map((serviceDate) => ({
        date: serviceDate?.date
          ? dayjs(onlyDateFromServer(`${serviceDate?.date}`)).format(DEFAULT_DATE_FORMAT)
          : undefined,
        isSuggested: serviceDate?.is_suggested, 
      })),
      accountOrders,
      availableAccounts,
      accountOrdersArraysWithUniqServices,
      isReceiptRequired: ocr?.is_receipt_required,
    };
  }, [mutateAsync]);

  const cancelOpenaiOcr = useCallback(() => {
    if (!controllerRef.current) {
      controllerRef.current = new AbortController();
    }
    controllerRef.current.abort();
  }, []);

  return {
    getDocumentOcr: handleGetDocumentOcr,
    cancelOpenaiOcr,
    isOcrLoading: isLoading,
  };
};

export default useGetDocumentOcrQuery;
